/**
 * Keeping keyboard focus when an action removes the focused control (a row that
 * leaves the list, a chip that's cleared, a button an @if takes away), which
 * otherwise drops focus to the top of the page.
 */

/** The element that actually takes focus: the input inside an Ionic searchbar, the native button inside an ion-button. */
export function focusTarget(el: Element): HTMLElement {
  const host = el as HTMLElement;
  if (host.tagName === 'ION-SEARCHBAR') return host.querySelector('input') ?? host;
  const inner = host.shadowRoot?.querySelector<HTMLElement>('button, input, a[href]');
  return inner ?? host;
}

/** Whether an element can take focus where it is now: on the page, not disabled, not hidden. */
export function canFocus(el: Element | null | undefined): el is HTMLElement {
  if (!el || !el.isConnected) return false;
  const host = el as HTMLElement;
  if ((host as HTMLButtonElement).disabled || host.getAttribute('aria-disabled') === 'true') return false;
  if (host.closest('[hidden], [aria-hidden="true"]')) return false;
  return true;
}

/** Focuses the first candidate that can take it (a heading gets tabindex -1 first). Returns it, or undefined. */
export function focusFirst(candidates: (Element | null | undefined)[]): HTMLElement | undefined {
  for (const c of candidates) {
    if (!canFocus(c)) continue;
    if (/^H[1-6]$/.test(c.tagName) && !c.hasAttribute('tabindex')) c.setAttribute('tabindex', '-1');
    const target = focusTarget(c);
    target.focus({ preventScroll: false });
    return target;
  }
  return undefined;
}

/** True when focus has nowhere useful: on the body, or on an element that's gone or disabled. */
export function focusLost(doc: Document, trigger?: Element | null): boolean {
  const active = doc.activeElement;
  if (!active || active === doc.body || active === doc.documentElement) return true;
  if (!active.isConnected) return true;
  return !!trigger && (active === trigger || trigger.contains(active)) && !canFocus(trigger);
}

export interface RestoreFocusOptions {
  /** How long to wait for the action to finish and the page to settle. */
  timeoutMs?: number;
  stepMs?: number;
  wait?: (ms: number) => Promise<unknown>;
}

/**
 * After an action started from `trigger`, waits until the trigger is removed (or
 * disabled while focused) and focus has been lost, then focuses the first of
 * `candidates()` that can take it. Leaves focus alone when the trigger stays,
 * or once the admin has moved focus somewhere else themselves.
 * Resolves to the element focused, or undefined.
 */
export async function restoreFocus(
  doc: Document,
  trigger: Element | null | undefined,
  candidates: () => (Element | null | undefined)[],
  { timeoutMs = 3000, stepMs = 50, wait = (ms) => new Promise((r) => setTimeout(r, ms)) }: RestoreFocusOptions = {},
): Promise<HTMLElement | undefined> {
  for (let waited = 0; waited <= timeoutMs; waited += stepMs) {
    await wait(stepMs);
    const active = doc.activeElement;
    const movedOn =
      !!active &&
      active !== doc.body &&
      active.isConnected &&
      !(trigger && (active === trigger || trigger.contains(active)));
    if (movedOn) return undefined;
    const gone = !trigger || !canFocus(trigger);
    if (gone && focusLost(doc, trigger)) return focusFirst(candidates());
  }
  return undefined;
}
